import { jsx as _jsx } from "react/jsx-runtime";
import { useStore } from 'jotai/react';
import { memo, useCallback, useMemo } from 'react';
import { Transformer } from 'react-konva';
import { stageRefAtom } from '@/store/boardAtoms';
import { clearGuides, getLineGuidedPositionForAnchorTransformer, } from '@/utils/board/snappingHelpers';
import { getSnappedXY, transformBoundBoxFunc } from '@/utils/board/stageUtils';
const BoardTransformer = ({ ref, zoom, keepRatio, gridSize, snapToGrid, onTransformEnd, onDragStart, onDragEnd, shouldOverdrawWholeArea = false, }) => {
    // Store для синхронного чтения атомов вне React-контекста
    const store = useStore();
    const rotateEnabled = zoom >= 0.05;
    const enabledAnchors = useMemo(() => {
        if (!rotateEnabled)
            return [];
        return keepRatio
            ? ['top-left', 'top-right', 'bottom-left', 'bottom-right']
            : [
                'top-left',
                'top-center',
                'top-right',
                'middle-left',
                'middle-right',
                'bottom-left',
                'bottom-center',
                'bottom-right',
            ];
    }, [keepRatio, rotateEnabled]);
    // Привязка якорей к сетке и гайдлайнам.
    // ВАЖНО: для гайдлайнов мы всегда считаем, что "курсор" находится в позиции самого якоря.
    // Поэтому в snapping-хелпер передаем фактическую позицию якоря, а не newPos от Konva.
    const anchorDragBoundFunc = useCallback((oldPos, newPos, e) => {
        const stage = store.get(stageRefAtom);
        if (!stage)
            return newPos;
        // Фактическая позиция якоря (реальный anchor), а не позиция курсора.
        const anchorNode = e.target;
        const anchorPos = anchorNode && typeof anchorNode.absolutePosition === 'function'
            ? anchorNode.absolutePosition()
            : newPos;
        // В snapping-хелпер передаём anchorPos как "позицию курсора", чтобы
        // логика снаппинга всегда работала относительно самого якоря.
        const guidedPos = getLineGuidedPositionForAnchorTransformer(stage, ref, e, anchorPos, oldPos);
        if (guidedPos)
            return guidedPos;
        // Если гайдлайны не сработали — используем существующий snap к сетке
        const { x, y } = getSnappedXY(stage, newPos.x, newPos.y, gridSize, snapToGrid);
        return Object.assign(Object.assign({}, newPos), { x, y });
    }, [store, gridSize, snapToGrid, ref]);
    // boundBoxFunc также мемоизируем — функция не должна пересоздаваться при каждом зуме.
    const boundBoxFunc = useCallback((oldBox, newBox) => transformBoundBoxFunc(oldBox, newBox, zoom), [zoom]);
    const rotateAnchorOffset = Math.max(10, 25 * zoom);
    const handleTransformEndInternal = useCallback((e) => {
        var _a;
        const layer = e.target.getLayer();
        if (layer) {
            clearGuides(layer);
            (_a = layer.getStage()) === null || _a === void 0 ? void 0 : _a.batchDraw();
        }
        onTransformEnd();
    }, [onTransformEnd]);
    return (_jsx(Transformer, { ref: ref, borderStrokeWidth: 1.5, borderStroke: "#0260bdff", anchorSize: 10, flipEnabled: false, rotateEnabled: rotateEnabled, rotationSnaps: [0, 45, 90, 135, 180, 225, 270, 315], rotationSnapTolerance: 5, shouldOverdrawWholeArea: shouldOverdrawWholeArea, anchorCornerRadius: 50, rotateLineVisible: false, rotateAnchorOffset: rotateAnchorOffset, ignoreStroke: true, anchorDragBoundFunc: anchorDragBoundFunc, boundBoxFunc: boundBoxFunc, 
        // onTransformMove={handleTransformMove}
        onTransformEnd: handleTransformEndInternal, onDragStart: onDragStart, onDragEnd: onDragEnd, keepRatio: keepRatio, enabledAnchors: enabledAnchors }));
};
export default memo(BoardTransformer);
